import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { adminApi } from '@/lib/adminApi';
import { feedbackApi } from '@/lib/feedbackApi';
import { CPIC_STATUSES, CPIC_STATUS_HEX, CPIC_SEVERITIES, CPIC_SEVERITY_HEX, filterCpic, genePhenotype, proposedCompound, type CpicCandidate } from '@/lib/cpic';
import { AdminPage, RegistryTable, Pill, FeedbackControl } from '@/admin';
import { CpicReviewModal } from '@/components/CpicReviewModal';

const REGISTRY = 'cpic_candidate';
const PAGE = 50;

export function CpicPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['cpic'], queryFn: () => adminApi.cpic.candidates() });
  const fb = useQuery({ queryKey: ['feedback-summary', REGISTRY], queryFn: () => feedbackApi.summary(REGISTRY) });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('unmapped');
  const [severity, setSeverity] = useState('');
  const [page, setPage] = useState(0);
  const [review, setReview] = useState<CpicCandidate | null>(null);

  const all = q.data?.candidates ?? [];
  const filtered = filterCpic(all, search, status, severity);
  const sevCounts = q.data?.severity_counts ?? {};
  const rows = filtered.slice(page * PAGE, page * PAGE + PAGE);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['cpic'] }); void qc.invalidateQueries({ queryKey: ['feedback-summary', REGISTRY] }); };
  const resetPage = () => setPage(0);

  const fetchSnapshot = useMutation({
    mutationFn: () => adminApi.cpic.fetch(),
    onSuccess: refresh,
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Fetch failed'),
  });
  const notRelevant = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => adminApi.cpic.markNotRelevant(id, reason),
    onSuccess: refresh,
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Error'),
  });
  const unlock = useMutation({
    mutationFn: (id: string) => adminApi.cpic.unlock(id),
    onSuccess: refresh,
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Error'),
  });

  return (
    <AdminPage
      title="Pharmacogenomic candidates (CPIC)"
      description="Drug–gene safety recommendations from CPIC (the Clinical Pharmacogenetics Implementation Consortium), staged for translation into Safety Index entries. Propose a mapping, then promote it into the live Safety Index."
      actions={<Button size="sm" disabled={fetchSnapshot.isPending} onClick={() => { if (confirm('Fetch the latest CPIC snapshot and rebuild candidates?')) fetchSnapshot.mutate(); }}>{fetchSnapshot.isPending ? 'Fetching…' : 'Fetch snapshot'}</Button>}
    >
      {q.data && !q.data.present ? <div className="mb-13 rounded border border-warning bg-warning-soft p-13 text-body-sm">No CPIC snapshot yet — run <strong>Fetch snapshot</strong> to build candidates.</div> : null}
      {q.data?.present ? (
        <div className="mb-13 flex flex-wrap items-center gap-8 text-body-sm text-text-secondary">
          <span><strong className="text-text-primary">{q.data.candidate_count ?? all.length}</strong> candidates
          {q.data.derived_at ? ` · derived ${q.data.derived_at}` : ''} · {filtered.length} in view</span>
          {CPIC_SEVERITIES.filter((s) => sevCounts[s]).map((s) => (
            <button key={s} type="button" onClick={() => { setSeverity(severity === s ? '' : s); resetPage(); }} title={`Filter ${s}`}>
              <Pill label={`${s} ${sevCounts[s]}`} color={CPIC_SEVERITY_HEX[s]} />
            </button>
          ))}
        </div>
      ) : null}

      <div className="mb-13 flex flex-wrap items-center gap-8">
        <input
          value={search} onChange={(e) => { setSearch(e.target.value); resetPage(); }}
          placeholder="Search drug, gene, phenotype…"
          className="w-[260px] rounded-sm border border-border-strong px-2.5 py-2 text-body-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        />
        <select value={status} onChange={(e) => { setStatus(e.target.value); resetPage(); }} className="rounded-sm border border-border-strong bg-surface px-2 py-2 text-body-sm">
          <option value="">All statuses</option>
          {CPIC_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={severity} onChange={(e) => { setSeverity(e.target.value); resetPage(); }} className="rounded-sm border border-border-strong bg-surface px-2 py-2 text-body-sm" aria-label="Severity">
          <option value="">All severities</option>
          {CPIC_SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <RegistryTable<CpicCandidate>
        columns={[
          { key: 'severity', header: 'Severity', render: (c) => c.severity ? <Pill label={c.severity} color={CPIC_SEVERITY_HEX[c.severity]} /> : <span className="text-text-muted">—</span> },
          { key: 'drug', header: 'Drug', className: 'font-medium', render: (c) => c.cpic?.drugname || '—' },
          { key: 'gene', header: 'Gene · Phenotype', className: 'text-[12px]', render: (c) => genePhenotype(c) },
          { key: 'compound', header: 'Compound', className: 'text-[12px] text-text-secondary', render: (c) => proposedCompound(c) || <span className="text-text-muted">— unset</span> },
          { key: 'class', header: 'Classification', className: 'text-[12px] text-text-secondary', render: (c) => c.cpic?.classification || '—' },
          { key: 'rec', header: 'Recommendation', className: 'max-w-[320px] text-[12px] text-text-secondary', render: (c) => c.cpic?.drugrecommendation ? (c.cpic.drugrecommendation.length > 160 ? `${c.cpic.drugrecommendation.slice(0, 159)}…` : c.cpic.drugrecommendation) : '—' },
          { key: 'status', header: 'Status', render: (c) => <Pill label={c.translation_status} color={CPIC_STATUS_HEX[c.translation_status]} /> },
        ]}
        rows={rows}
        getRowKey={(c) => c.candidate_id}
        count={filtered.length}
        state={{ loading: q.isLoading, error: q.isError }}
        emptyText={q.data?.present ? 'No candidates match these filters.' : 'No candidates — fetch a snapshot.'}
        rowActions={(c) => (
          <div className="flex items-center gap-2">
            <FeedbackControl registry={REGISTRY} itemId={c.candidate_id} itemLabel={c.cpic?.drugname || c.candidate_id} openCount={fb.data?.[c.candidate_id]?.open ?? 0} onChanged={refresh} />
            {c.translation_status === 'promoted' || c.translation_status === 'not_relevant' ? (
              <Button variant="outline" size="sm" disabled={unlock.isPending} onClick={() => unlock.mutate(c.candidate_id)}>Unlock</Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => setReview(c)}>Review</Button>
                <Button variant="outline" size="sm" onClick={() => { const r = prompt('Reason for marking not relevant?'); if (r !== null) notRelevant.mutate({ id: c.candidate_id, reason: r }); }}>Not relevant</Button>
              </>
            )}
          </div>
        )}
      />

      {filtered.length > PAGE ? (
        <div className="mt-13 flex items-center justify-between text-body-sm">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>← Prev</Button>
          <span className="text-text-secondary">{page * PAGE + 1}–{Math.min((page + 1) * PAGE, filtered.length)} of {filtered.length}</span>
          <Button variant="outline" size="sm" disabled={(page + 1) * PAGE >= filtered.length} onClick={() => setPage(page + 1)}>Next →</Button>
        </div>
      ) : null}

      {review ? <CpicReviewModal candidate={review} onClose={() => setReview(null)} /> : null}
    </AdminPage>
  );
}
